<script lang="ts">
  import { faPlus, faTrash, faWarning } from '@fortawesome/free-solid-svg-icons';
  import Fa from 'svelte-fa';
  import { cloneDeep, isEqual } from 'es-toolkit';

  import { page } from '$app/state';
  import { browser } from '$app/env';

  import MaintainerStatus from '#lib/components/MaintainerStatus.svelte';
  import type { datacenter as dcSchema } from '#lib/server/drizzle/schema.js';

  import AddDatacenterForm from './AddDatacenterModal.svelte';
  import DeleteDatacenterForm from './DeleteDatacenterModal.svelte';
  import AddSystemForm from './AddSystemModal.svelte';

  let { data } = $props();

  let datacenters = $derived(cloneDeep(data.datacenters));
  let dirty = $derived(!isEqual(datacenters, data.datacenters));

  let showDisabled = $state(browser && localStorage.getItem('showDisabled') === 'true');
  $effect(() => {
    localStorage.setItem('showDisabled', String(showDisabled));
  });

  let addDatacenterModalOpen = $state(false);
  let deleteDatacenterModalOpen = $state<{ datacenter?: typeof dcSchema.$inferSelect }>();
  let addSystemModalOpen = $state<{ datacenter?: typeof dcSchema.$inferSelect }>();
</script>

<h1>Storage configuration</h1>

<section class="mb-2 flex items-center gap-4 p-3">
  <label class="flex flex-row items-center gap-2">
    <input
      type="checkbox"
      bind:checked={showDisabled}
      name="show-inactive"
      class="toggle toggle-primary"
    />
    Display inactive
  </label>
  {#if dirty}
    <Fa icon={faWarning} />
  {/if}
</section>

<table class="table-zebra table-sm no-grid table">
  <thead>
    <tr>
      <th>System</th>
      {#if page.data.feMode === 'hp'}
        <th class="text-center!">Array model</th>
        <th>Physical Serial Number</th>
        <th>Virtual DKCs</th>
        <th>Management IP</th>
        <th>Room</th>
        <th>Rack</th>
      {/if}
      <th>Sort ID</th>
      <th>Active</th>
    </tr>
  </thead>
  <tbody>
    {#each data.datacenters as dc (dc.id)}
      <tr>
        <td class="text-tertiary-800 dark:text-tertiary-200 text-lg font-extrabold">
          {dc.name}
        </td>
        {#each { length: page.data.feMode === 'hp' ? 7 : 1 }}
          <td></td>
        {/each}
        <td>
          <div class="flex items-center justify-center">
            {#if dc.name !== 'Unassigned'}
              <button
                class="btn-icon btn-icon-sm text-error-500 p-1"
                onclick={() => (deleteDatacenterModalOpen = { datacenter: dc })}
                ><Fa icon={faTrash} /></button
              >
            {/if}
          </div>
        </td>
      </tr>
      {#each dc.systems as system (system.id)}
        <tr class={!system.active && !showDisabled ? 'hidden!' : ''}>
          <td>
            <a class="pl-4 underline" href="/storage-config/{system.id}">{system.name}</a>
            <MaintainerStatus status={data.statusMap.get(system.name) ?? { status: 'offline' }} />
          </td>
          {#if page.data.feMode === 'hp'}
            <td class="text-center!">{system.hpDetails?.model ?? '-'}</td>
            <td class="text-center!">{system.hpDetails?.serialNumber ?? '-'}</td>
            <td class="text-center!">{system.hpDetails?.dkc ?? '-'}</td>
            <td class="text-center!">{system.hpDetails?.managementIp ?? '-'}</td>
            <td class="text-center!">{system.hpDetails?.room ?? '-'}</td>
            <td class="text-center!">{system.hpDetails?.rack ?? '-'}</td>
            <!-- {:else if $page.data.feMode === 'emc'} -->
          {/if}
          <td class="text-center!">{system.sortId}</td>
          <td class="text-center!">
            <input
              type="checkbox"
              class="checkbox checkbox-xs checkbox-primary"
              checked={system.active}
              readonly
              onclick={(e) => e.preventDefault()}
            />
          </td>
        </tr>
      {/each}
      <tr class="bg-transparent!">
        <td>
          <button
            class="btn btn-sm btn-ghost btn-primary ml-4"
            onclick={() => (addSystemModalOpen = { datacenter: dc.id < 0 ? undefined : dc })}
            ><Fa icon={faPlus} />Add a system</button
          >
        </td>
        {#each { length: page.data.feMode === 'hp' ? 8 : 2 }}
          <td></td>
        {/each}
      </tr>
    {/each}
  </tbody>
</table>

<button class="btn btn-soft w-full" onclick={() => (addDatacenterModalOpen = true)}
  ><Fa icon={faPlus} />Add a datacenter</button
>

<AddDatacenterForm bind:open={addDatacenterModalOpen} />
<DeleteDatacenterForm
  bind:open={
    () => Boolean(deleteDatacenterModalOpen),
    (v) => (deleteDatacenterModalOpen = v ? {} : undefined)
  }
  datacenter={deleteDatacenterModalOpen?.datacenter}
/>
<AddSystemForm
  bind:open={() => Boolean(addSystemModalOpen), (v) => (addSystemModalOpen = v ? {} : undefined)}
  datacenter={addSystemModalOpen?.datacenter}
  systems={data.datacenters.flatMap((dc) => dc.systems)}
/>
